/**
 * Company assignment for report profiles.
 * Default: all companies. Pick one company to scope assignment.
 * When the profile has a target role, backend assigns only where invited
 * participants match that designation.
 */

export const ASSIGNMENT_SCOPES = {
  ALL: "all",
  ONE: "one",
};

export const ASSIGN_ALL_VALUE = "__all_companies__";

export function defaultAssignmentPrefs(plan = null) {
  if (plan?.assign_to_company) {
    return {
      companyScope: ASSIGNMENT_SCOPES.ONE,
      companyName: plan.assign_to_company,
    };
  }
  return { companyScope: ASSIGNMENT_SCOPES.ALL, companyName: "" };
}

export function assignmentPrefsToPlanPatch(prefs, isTeamBase) {
  const assignAs = isTeamBase ? "team_report" : "company";
  const name = (prefs?.companyName || "").trim();
  if (prefs?.companyScope === ASSIGNMENT_SCOPES.ONE && name) {
    return {
      assign_to_company: name,
      assign_to_all_companies: false,
      assign_as: assignAs,
    };
  }
  return {
    assign_to_company: null,
    assign_to_all_companies: true,
    assign_as: assignAs,
  };
}

export function assignmentSummary(prefs, companies = []) {
  if (prefs?.companyScope === ASSIGNMENT_SCOPES.ONE) {
    const name = (prefs.companyName || "").trim();
    return name || null;
  }
  const n = companies.length;
  return n ? `All companies (${n})` : "All companies";
}

function prefsToSelectValue(prefs) {
  if (prefs?.companyScope === ASSIGNMENT_SCOPES.ONE && prefs.companyName) {
    return prefs.companyName;
  }
  return ASSIGN_ALL_VALUE;
}

function selectValueToPrefs(value) {
  if (!value || value === ASSIGN_ALL_VALUE) {
    return { companyScope: ASSIGNMENT_SCOPES.ALL, companyName: "" };
  }
  return { companyScope: ASSIGNMENT_SCOPES.ONE, companyName: value };
}

function roleHint(targetRole, isTeamBase) {
  const slot = isTeamBase ? "team report" : "company report";
  if (targetRole) {
    return `Applies to invited ${targetRole} participants — sets the ${slot} for matching companies when you save.`;
  }
  return `Sets the ${slot} template for selected companies when you save.`;
}

export default function ProfileAssignmentForm({
  isTeamBase,
  targetRole = "",
  companies = [],
  value,
  onChange,
  loadingCompanies = false,
  compact = false,
  variant = "default",
  idPrefix = "profile-assign",
  selectClassName = "",
}) {
  const prefs = value || defaultAssignmentPrefs();
  const slotLabel = isTeamBase ? "team report" : "company report";

  const setPrefs = (patch) => onChange({ ...prefs, ...patch });

  const handleScope = (scope) => {
    setPrefs({
      companyScope: scope,
      companyName: scope === ASSIGNMENT_SCOPES.ONE ? prefs.companyName : "",
    });
  };

  if (variant === "builder") {
    const selectValue = prefsToSelectValue(prefs);
    const fieldClass =
      selectClassName ||
      "w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#004f73]/40";

    return (
      <div>
        <label
          htmlFor={`${idPrefix}-company`}
          className="block text-xs font-semibold text-gray-600 mb-1"
        >
          Assign to companies
        </label>
        <select
          id={`${idPrefix}-company`}
          value={selectValue}
          disabled={loadingCompanies}
          onChange={(e) => onChange(selectValueToPrefs(e.target.value))}
          className={fieldClass}
        >
          <option value={ASSIGN_ALL_VALUE}>All companies</option>
          {companies.length > 0 ? (
            <option disabled>──────────────</option>
          ) : null}
          {companies.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
        <p className="text-[10px] text-gray-400 mt-1 leading-snug">
          {roleHint(targetRole, isTeamBase)}
        </p>
      </div>
    );
  }

  const pad = compact ? "space-y-2" : "space-y-3";
  const labelClass = compact
    ? "text-[11px] font-medium text-gray-600"
    : "text-xs font-semibold text-gray-600";

  return (
    <div className={`rounded-lg border border-gray-200 bg-gray-50/80 p-3 ${pad}`}>
      <span className={labelClass}>Assign to companies</span>
      <p className="text-[10px] text-gray-500 mt-0.5 leading-snug mb-2">
        {roleHint(targetRole, isTeamBase)}
      </p>
      <div className="flex flex-wrap gap-2">
        {[
          { id: ASSIGNMENT_SCOPES.ALL, label: "All companies" },
          { id: ASSIGNMENT_SCOPES.ONE, label: "One company" },
        ].map((opt) => (
          <button
            key={opt.id}
            type="button"
            onClick={() => handleScope(opt.id)}
            className={`text-[11px] px-2.5 py-1 rounded-full border font-medium transition-colors ${
              prefs.companyScope === opt.id
                ? "bg-[#004f73] text-white border-[#004f73]"
                : "bg-white text-gray-600 border-gray-200 hover:border-[#004f73]/40"
            }`}
          >
            {opt.label}
          </button>
        ))}
      </div>

      {prefs.companyScope === ASSIGNMENT_SCOPES.ONE ? (
        <select
          value={prefs.companyName}
          disabled={loadingCompanies}
          onChange={(e) => setPrefs({ companyName: e.target.value })}
          className="w-full text-xs border border-gray-300 rounded-lg px-2.5 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-[#004f73]/30"
        >
          <option value="">Select company…</option>
          {companies.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
      ) : (
        <p className="text-[10px] text-gray-500">
          {loadingCompanies
            ? "Loading companies…"
            : companies.length
              ? `Will assign to all ${companies.length} companies in the workspace.`
              : "No companies in the workspace yet."}
        </p>
      )}
    </div>
  );
}
